// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

export type FileSortMode = 'nameAsc' | 'nameDesc' | 'dateNewest' | 'dateOldest';

export const FILE_SORT_OPTIONS: { value: FileSortMode; label: string }[] = [
  { value: 'nameAsc', label: 'Name (A-Z)' },
  { value: 'nameDesc', label: 'Name (Z-A)' },
  { value: 'dateNewest', label: 'Newest' },
  { value: 'dateOldest', label: 'Oldest' },
];

// path is expected to be non-empty (every file record's filename is a required field on the
// server), but a legacy record predating that constraint - or any other falsy input - degrades to
// an empty name instead of throwing.
export const basename = (path?: string | null): string => (path ? path.split('/').pop() || path : '');

// Numeric-aware and case-insensitive, so "file2" sorts before "file10" and casing doesn't
// scatter otherwise-identical names apart.
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

interface IFileSortKeys {
  name?: string | null;
  createdAt?: string | Date | null;
}

// A missing or unparsable date (e.g. an empty string) falls back to epoch 0 - oldest, not NaN,
// which would otherwise make every comparison touching that file return NaN and leave its
// position undefined instead of sorted to one consistent end.
const dateTime = (value?: string | Date | null): number => {
  const time = new Date(value ?? 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const compareByDate = (a: IFileSortKeys, b: IFileSortKeys): number => dateTime(a.createdAt) - dateTime(b.createdAt);

const compareFileKeys = (mode: FileSortMode) => (a: IFileSortKeys, b: IFileSortKeys): number => {
  const nameA = a.name ?? '';
  const nameB = b.name ?? '';
  switch (mode) {
  case 'nameDesc':
    return collator.compare(nameB, nameA);
  case 'dateNewest':
    return compareByDate(b, a);
  case 'dateOldest':
    return compareByDate(a, b);
  case 'nameAsc':
  default:
    return collator.compare(nameA, nameB);
  }
};

// Returns a new, sorted array - getKeys lets each caller's own file shape (an already-derived
// display name for some types, a raw storage path for others) feed the comparator without
// forcing every file type into one shared object shape.
export const sortFiles = <T>(files: T[], mode: FileSortMode, getKeys: (file: T) => IFileSortKeys): T[] => {
  const compare = compareFileKeys(mode);
  return [...files].sort((a, b) => compare(getKeys(a), getKeys(b)));
};
